// Active storefront campaign — single source of truth for code, dates, pricing.
//
// This replaces the hardcoded VERANO30 module. That module's window closed on
// 2026-09-01 and nothing took its place, so `isCampaignActive()` went false and
// the cart STOPPED auto-applying any discount — every shopper from Sept 1 on had
// to find and manually type a code. OTONO30 was created in Shopify but the
// storefront never learned about it. Keep this list updated when a campaign
// turns over; `verano30.ts` re-exports from here so old call sites keep working.

export interface StorefrontCampaign {
  code: string;
  /** Fraction off, used for displayed "precio con descuento". */
  discountFraction: number;
  /** UTC ms. Mexico CST = UTC-6. */
  start: number;
  end: number;
  /** Seasonal noun for badges: `Oferta de ${season}`. Keep it capitalised. */
  season: string;
  /** Human deadline for urgency copy, e.g. "31 de octubre". Must match email. */
  deadlineLabel: string;
  /** Headline for the arrival banner shown to `?discount=<code>` traffic. */
  headline: string;
  blurb: string;
  /** Homepage hero takeover copy, used while the campaign runs. */
  heroBadge: string;
  heroHeadline: string;
  heroSubcopy: string;
  /** Site-wide promo bar (every page, above the header). */
  promoBarText: string;
  promoBarHref: string;
  /** Homepage about/CTA section overrides while the campaign runs. */
  aboutTitle: string;
  aboutDescription: string;
  ctaTitle: string;
  ctaSubtitle: string;
  /** Line above the homepage social-proof stats. */
  trustStripLine: string;
  /** Optional seasonal explainer under the hero. Null when the season has no
   *  physiological story to tell (Buen Fin is a date, not a hair cycle). */
  educationStrip: {
    eyebrow: string;
    title: string;
    body: string;
    kicker: string;
  } | null;
}

const CAMPAIGNS: StorefrontCampaign[] = [
  {
    // Otoño 30% — hard stop Oct 31 (Jorge, 2026-10-19). The deadline is live in
    // 48 Klaviyo flows and in the Meta creative, so it must not quietly slip.
    code: 'OTONO30',
    discountFraction: 0.30,
    start: new Date('2026-10-01T06:00:00Z').getTime(),
    end: new Date('2026-11-01T05:59:59Z').getTime(),
    season: 'Otoño',
    deadlineLabel: '31 de octubre',
    headline: 'Este otoño, 30% OFF en tu nutrición capilar',
    blurb: 'La caída estacional de otoño es real. Olin nutre tu cabello desde el folículo con 17 ingredientes.',
    heroBadge: 'Promo Otoño 2026 · 30% OFF',
    // Copy as it already runs in production (feat/otono30-campaign-transition,
    // Oct 5) — kept verbatim so this refactor changes plumbing, not messaging.
    heroHeadline: 'Tu cabello se cae más en otoño — fortalécelo desde adentro con 30% OFF',
    heroSubcopy:
      'El cambio de estación acelera la caída. Olin lo nutre con 17 ingredientes desde adentro para que pase el otoño fuerte.',
    promoBarText: '30% DE DESCUENTO CON OTONO30 · VENCE EL 31 DE OCTUBRE',
    promoBarHref: '/comprar',
    aboutTitle: 'En otoño tu cabello necesita más — nutre desde adentro.',
    aboutDescription:
      'La caída estacional es real. Olin combina 17 ingredientes para fortalecer tu cabello cuando más lo necesita.',
    ctaTitle: 'Este otoño, fortalece tu cabello con 30% OFF',
    ctaSubtitle: 'Código OTONO30 · vence el 31 de octubre.',
    trustStripLine:
      'En otoño la caída estacional se intensifica — estos son los resultados reales con Olin:',
    educationStrip: {
      eyebrow: 'Dato de temporada',
      title: '¿Por qué se te cae más el pelo en otoño?',
      body: 'En verano, más folículos entran en fase de reposo para proteger el cuero cabelludo del sol. Como esa fase dura ~3 meses, el cabello se suelta en otoño. Es normal — pero puedes nutrirlo desde adentro para que el nuevo ciclo arranque más fuerte.',
      kicker: '30% OFF con OTONO30 para empezar hoy',
    },
  },
  {
    // Buen Fin 2026 — Nov 13-17. NOTE: the campaign is tiered (BUENFIN40 on the
    // 90-day kit, BUENFIN30 on everything else). This auto-apply path can only
    // carry ONE code, so it applies the storewide 30%; the 40% kit tier needs
    // either a Shopify automatic discount on that product or a per-product code
    // at the buy button. Do not ship Buen Fin assuming 40% applies here.
    code: 'BUENFIN30',
    discountFraction: 0.30,
    start: new Date('2026-11-13T06:00:00Z').getTime(),
    end: new Date('2026-11-18T05:59:59Z').getTime(),
    season: 'Buen Fin',
    deadlineLabel: '17 de noviembre',
    headline: 'Buen Fin: 30% OFF en tu nutrición capilar',
    blurb: 'Cinco días para empezar tu plan de 90 días al mejor precio del año.',
    heroBadge: 'Buen Fin 2026 · 30% OFF',
    heroHeadline: 'Cabello más fuerte desde adentro — 30% OFF en el Buen Fin',
    heroSubcopy:
      'Cinco días para empezar tu plan de 90 días. Olin nutre tu cabello con 17 ingredientes desde adentro.',
    promoBarText: 'BUEN FIN · 30% DE DESCUENTO CON BUENFIN30 · HASTA EL 17 DE NOVIEMBRE',
    promoBarHref: '/comprar',
    aboutTitle: 'El suplemento que suma a tu rutina de cuidado personal.',
    aboutDescription: 'Nuestra fórmula está diseñada para nutrir desde dentro.',
    ctaTitle: 'Buen Fin: empieza tu plan con 30% OFF',
    ctaSubtitle: 'Código BUENFIN30 · hasta el 17 de noviembre.',
    trustStripLine: 'Estos son los resultados reales con Olin:',
    // Buen Fin is a retail date, not a hair-cycle event — no seasonal biology
    // claim to make, so no explainer strip.
    educationStrip: null,
  },
];

/** The campaign running right now, or null in a deliberate quiet window. */
export function getActiveCampaign(now: number = Date.now()): StorefrontCampaign | null {
  return CAMPAIGNS.find(c => now >= c.start && now < c.end) ?? null;
}

export function isCampaignActive(now: number = Date.now()): boolean {
  return getActiveCampaign(now) !== null;
}

/** Active campaign code, or null when no campaign is running. */
export function getCampaignCode(now: number = Date.now()): string | null {
  return getActiveCampaign(now)?.code ?? null;
}

/** Price after the active campaign discount, rounded. Unchanged when none runs. */
export function campaignPrice(shopifyAmount: string, now: number = Date.now()): number {
  const original = parseFloat(shopifyAmount);
  const campaign = getActiveCampaign(now);
  if (!campaign) return Math.round(original);
  return Math.round(original * (1 - campaign.discountFraction));
}

/** Per-day cost string (e.g. "18.4") given a total price and number of days. */
export function perDayCost(price: number, days: number): string {
  return (price / days).toFixed(1);
}

/** Discount as a whole-percent string without the sign, e.g. "30". */
export function campaignPercent(now: number = Date.now()): string {
  const campaign = getActiveCampaign(now);
  return String(Math.round((campaign?.discountFraction ?? 0) * 100));
}

/** True if `code` belongs to a seasonal campaign in this file (live or not). */
export function isSeasonalCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const upper = code.toUpperCase();
  return CAMPAIGNS.some(c => c.code === upper);
}

/**
 * Resolve the code a stored surface should advertise — landing pages persist
 * `discount_code` at generation time, so a page built in July still said
 * VERANO30 in October and would advertise a dead code the moment that discount
 * is disabled in Shopify.
 *
 *  - campaign running  → the live campaign code wins over whatever was stored
 *  - quiet window      → a stored SEASONAL code is dropped (it has expired),
 *                        while a non-seasonal evergreen code (PERFIL15 …) stays
 */
export function resolveStoredDiscountCode(
  stored: string | null | undefined,
  now: number = Date.now(),
): string | undefined {
  const live = getCampaignCode(now);
  if (live) return live;
  if (isSeasonalCode(stored)) return undefined;
  return stored ?? undefined;
}

/**
 * Offer sentence for surfaces that promise a discount before the cart exists
 * (quiz report, scanner result, profile bridge). Returns null in a quiet window
 * so callers fall back to their evergreen offer instead of promising a dead code.
 */
export function campaignOfferLine(now: number = Date.now()): string | null {
  const campaign = getActiveCampaign(now);
  if (!campaign) return null;
  return `Empieza hoy con ${campaignPercent(now)}% OFF — el código ${campaign.code} se aplica solo al carrito (vence el ${campaign.deadlineLabel}).`;
}
